/**
 * Utility functions for formatting currency values with proper symbols
 */

/**
 * THE PLATFORM'S THOUSANDS SEPARATOR.
 *
 * Marin, 2026-08-02: "für jede Währung Apostrophen ' verwenden". Every
 * currency, every surface, every viewer — €12'500'000, CHF 12'500'000,
 * $12'500'000.
 *
 * WHY IT IS PINNED HERE INSTEAD OF LEFT TO THE BROWSER. This module used to
 * group with `toLocaleString(undefined, …)`, i.e. in the VIEWER'S locale, so
 * the same pitch read "€12,500,000" in New York, "€12.500.000" in Berlin and
 * "€12'500'000" in Zurich. Two users comparing the same figure saw two
 * different strings, and a screenshot in a chat meant nothing without knowing
 * whose machine it came from. A price is a fact about the deal, not about the
 * reader.
 *
 * IT IS AN ASCII APOSTROPHE (U+0027), NOT the typographic U+2019 that ICU's
 * de-CH locale emits. Deriving the character from a locale would put us back
 * where we started — the glyph would then depend on the runtime's CLDR
 * version, and it would not survive a copy-paste into a spreadsheet or a
 * search box. One literal character, decided here, is the whole point.
 */
export const THOUSANDS_SEPARATOR = "'";

/**
 * THE DECIMAL SEPARATOR: a dot.
 *
 * The apostrophe's native partner. Swiss notation itself is "12'500'000.50" —
 * apostrophe groups, dot decimal — so the pair Marin asked for is already a
 * coherent convention rather than an invention of ours. It also matches what
 * the platform stores and transports: the backend's DecimalField serialises
 * "12500000.50", and every amount input on the platform accepts a dot. A
 * comma decimal beside an apostrophe group ("12'500'000,50") is a combination
 * no locale on earth uses, and it would disagree with the very digits the
 * user typed.
 *
 * It is not configurable: `formatGroupedNumber` gets it from en-US, which
 * uses a dot, and only the GROUP character is rewritten below.
 */
export const DECIMAL_SEPARATOR = ".";

/**
 * The one place a number becomes a grouped string on this platform.
 *
 * Formats in a FIXED locale (en-US) and then rewrites its group character to
 * the apostrophe. Fixing the locale is what makes the output identical for
 * every viewer; en-US specifically because it groups with "," and never uses
 * "," as a decimal mark, so the rewrite below cannot corrupt a fraction.
 *
 * Everything on the platform that groups digits into thousands must come
 * through here — a second grouping helper with a different separator would be
 * worse than the locale-dependent state this replaced, because it would be
 * inconsistent WITHIN one screen instead of merely between two machines.
 *
 * @param {number|string} value - the amount
 * @param {object} [options] - Intl.NumberFormat options (fraction digits, …)
 * @returns {string} grouped digits, or "" when the value is not a number
 */
export const formatGroupedNumber = (value, options = undefined) => {
  const numericValue = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(numericValue)) return "";
  return numericValue.toLocaleString("en-US", options).replace(/,/g, THOUSANDS_SEPARATOR);
};

/**
 * Currency symbols mapping.
 *
 * INR stays in this table ON PURPOSE even though the Rupee is no longer
 * offered anywhere (2026-08-01: pitch fees are EUR/USD/GBP/CHF). A pitch stored
 * with 'INR' before that change still has to render, and "₹1,000,000" is the
 * honest reading of that row — dropping the symbol would print a bare number
 * or, worse, let it be mistaken for another currency. Removing a symbol
 * removes the ability to READ old data; removing a CHOICE only stops new data.
 */
export const CURRENCY_SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  INR: "₹",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
  CHF: "CHF",
};

// A second CURRENCY_CHOICES list used to sit here. It had no importer — every
// dropdown in the app reads constants/tenderChoices.js (or the agent-tender
// re-export) — and it still offered INR, JPY, CAD and AUD, which the catalogue
// dropped on 2026-08-02 (constants/tenderChoices.currency.test.js pins that
// EUR/USD/GBP/CHF are the whole list and that the Rupee must not be
// selectable). A dead copy of a catalogue can only drift away from the live
// one, so it is gone. CURRENCY_SYMBOLS above is a different thing and stays:
// it is what lets an OLD row stored in INR still be read.

/**
 * Format a currency value with its symbol
 * @param {number|string} value - The amount to format
 * @param {string} currencyCode - The currency code (USD, EUR, etc.)
 * @param {boolean} showCode - Whether to show the currency code after the value
 * @returns {string} Formatted currency string
 */
export const formatCurrencyWithSymbol = (value, currencyCode = "USD", showCode = false) => {
  // Treat only null/undefined/empty as missing; a real 0 must render (e.g. a salary range from 0).
  if (value === null || value === undefined || value === "") return "-";

  // Some stored rows carry the SYMBOL instead of a code (TheSports market-value
  // sync writes ts_market_value_currency = "€"). A known symbol passes through
  // as-is; an unknown code still renders the bare number rather than inventing
  // a currency.
  const isKnownSymbol = Object.values(CURRENCY_SYMBOLS).includes(currencyCode);
  const symbol = isKnownSymbol
    ? currencyCode
    : CURRENCY_SYMBOLS[currencyCode] || "";
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) return "-";

  // A LETTER-based symbol needs a space; a glyph does not. "CHF12,500,000" runs
  // the code into the digits and misreads at a glance, while "€12,500,000" is
  // correct tight. CHF is the only such symbol in the table today (C$/A$/₹/¥
  // all end in a glyph), and this is the same rendering the platform's other
  // fee surfaces already chose by hand — utils/scoutFeeFrame.js and
  // utils/lastMinute.js both spell it `CHF: "CHF "` ("up to CHF 800k").
  //
  // Expressed as a RULE, not as `if (code === "CHF")`, so the next lettered
  // currency renders right without anyone remembering this line. The symbol
  // table itself stays clean of padding — utils/agentRequestFigures.js
  // concatenates those symbols on its own and wants them unpadded.
  const separator = /[A-Za-z]$/.test(symbol) ? " " : "";

  // Format the number with appropriate decimals.
  //
  // Grouping goes through formatGroupedNumber, so the apostrophe is the same
  // one every other money surface uses and the result does NOT depend on the
  // viewer's locale (Marin, 2026-08-02: "für jede Währung Apostrophen ' ").
  //
  // JPY keeps its own branch only because the Yen has no minor unit: it asks
  // for no fraction digits at all rather than "up to 2". The separator is the
  // same for it as for everything else.
  const formattedValue = currencyCode === "JPY"
    ? formatGroupedNumber(numericValue)
    : formatGroupedNumber(numericValue, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });


  // Return formatted string with symbol
  return `${symbol}${separator}${formattedValue}${showCode ? ` ${currencyCode}` : ""}`;
};

/**
 * Format a PITCH transfer fee.
 *
 * Same formatter, one difference: a pitch whose currency is missing reads as
 * EUR, not as the generic USD default above. EUR is the platform's default
 * currency for a pitch fee (it is the form's pre-selection, and the backend's
 * own symbol helpers already fall back to EUR), so a stored row that never
 * recorded one is far likelier to have meant Euro than Dollar.
 *
 * It exists as its own function so the pitch surfaces share ONE fallback
 * instead of repeating `|| "EUR"`, and so the generic default that other
 * surfaces (requests, salaries, market values) rely on stays untouched.
 *
 * A legacy 'INR' fee is passed straight through and renders with ₹.
 *
 * @param {number|string} value - The fee
 * @param {string} currencyCode - Stored currency, may be null/empty
 * @returns {string} Formatted fee
 */
export const formatTransferFee = (value, currencyCode) =>
  formatCurrencyWithSymbol(value, currencyCode || "EUR");